import { useState, useEffect, useCallback } from 'react';

interface UseDragAndDropProps<T> {
  items: T[];
  onReorder: (newItems: T[]) => void;
  direction?: 'horizontal' | 'vertical';
}

interface DragState {
  draggedIndex: number | null;
  isDragging: boolean;
}

/**
 * Custom hook for drag and drop functionality
 * Simple implementation using mouse events for desktop-only drag & drop
 */
function useDragAndDrop<T>({
  items,
  onReorder,
  direction = 'horizontal'
}: UseDragAndDropProps<T>) {
  const [dragState, setDragState] = useState<DragState>({
    draggedIndex: null,
    isDragging: false
  });

  const [localItems, setLocalItems] = useState<T[]>(items);

  // Update local items when props change
  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  // Start dragging
  const handleDragStart = useCallback((index: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    setDragState({
      draggedIndex: index,
      isDragging: true
    });
  }, []);

  // Handle dragging over an item - swap positions immediately
  const handleDragOver = useCallback((targetIndex: number) => {
    if (!dragState.isDragging || dragState.draggedIndex === null) return;
    if (dragState.draggedIndex === targetIndex) return;

    // Perform swap
    const newItems = [...localItems];
    const draggedItem = newItems[dragState.draggedIndex];
    newItems.splice(dragState.draggedIndex, 1);
    newItems.splice(targetIndex, 0, draggedItem);

    setLocalItems(newItems);
    setDragState({
      draggedIndex: targetIndex,
      isDragging: true
    });
  }, [dragState.draggedIndex, dragState.isDragging, localItems]);

  // End dragging and save
  const handleDragEnd = useCallback(() => {
    if (dragState.isDragging && dragState.draggedIndex !== null) {
      // Call the onReorder callback with final order
      onReorder(localItems);
    }

    setDragState({
      draggedIndex: null,
      isDragging: false
    });
  }, [dragState.isDragging, dragState.draggedIndex, localItems, onReorder]);

  // Listen for global mouseup to end dragging
  useEffect(() => {
    if (dragState.isDragging) {
      const handleMouseUp = () => {
        handleDragEnd();
      };

      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState.isDragging, handleDragEnd]);

  // Prevent text selection during drag
  useEffect(() => {
    if (dragState.isDragging) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }

    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [dragState.isDragging]);

  // Get props for draggable item
  const getDraggableProps = useCallback((index: number) => ({
    onMouseDown: handleDragStart(index),
    onMouseEnter: () => handleDragOver(index),
    style: {
      cursor: dragState.isDragging && dragState.draggedIndex === index ? 'grabbing' : 'grab',
      opacity: dragState.isDragging && dragState.draggedIndex === index ? 0.5 : 1,
      transition: dragState.isDragging ? 'none' : 'all 0.2s ease'
    },
    'data-draggable-index': index
  }), [dragState.draggedIndex, dragState.isDragging, handleDragStart, handleDragOver]);

  return {
    items: localItems,
    draggedIndex: dragState.draggedIndex,
    isDragging: dragState.isDragging,
    getDraggableProps,
    handleDragEnd
  };
}

export default useDragAndDrop;
